/**
 * Modulos que hoy no tienen datos, con el motivo de cada uno.
 *
 * Por que existe esta seccion en vez de tres tarjetas mas
 * ------------------------------------------------------
 * `campaigns`, `pricing_history` y `competitor_prices` tienen cero filas. Tres
 * tarjetas grandes diciendo "0" al lado de las de catalogo harian que el
 * tablero entero se lea como roto -- y ademas serian el tipo de cero que no se
 * puede interpretar: nadie sabe si es "no paso nada", "no se uso" o "se rompio
 * la consulta".
 *
 * Los numeros se leen igual (de `GET /api/v1/stores/{id}/stats`, que los cuenta
 * de verdad) pero se muestran en una lista, y cuando valen cero se acompanan
 * con la razon **verificada en el codigo** por la que estan vacios. Si algun
 * dia dejan de estar en cero, la razon desaparece sola y queda el numero: no
 * hay nada hardcodeado.
 */

import type { ReactNode } from "react";

import type { StoreStats } from "../../api/types";
import { formatCount } from "./format";
import { Note, Panel } from "./ui";

interface QuietModule {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  /** Texto complementario cuando SI hay filas. */
  readonly filled: string;
  /** Por que esta vacio. Verificado en el codigo, no supuesto. */
  readonly whyEmpty: ReactNode;
}

export function QuietModules({ stats }: { readonly stats: StoreStats }) {
  const modules: readonly QuietModule[] = [
    {
      id: "campaigns",
      label: "Campanas",
      value: stats.campaign_count,
      filled: `${formatCount(stats.active_campaign_count)} activas.`,
      whyEmpty: (
        <>
          Nunca se creo ninguna. El backend las soporta (
          <code>/api/v1/campaigns</code> y las tasks{" "}
          <code>campaign.activate_scheduled</code> /{" "}
          <code>campaign.deactivate_expired</code>), pero ninguna pantalla del
          panel las usa todavia y no hay una sola fila en la tabla.
        </>
      ),
    },
    {
      id: "pricing-history",
      label: "Historial de precios",
      value: stats.pricing_history_count,
      filled: "Cambios de precio registrados para esta tienda.",
      whyEmpty: (
        <>
          Se escribe cuando el <code>PricingEngine</code> aplica un cambio de
          precio o cuando una campana ajusta precios. Todavia no se aplico
          ninguno: la pantalla de Pricing es de solo lectura a proposito, porque
          con los costos en cero el precio optimo que calcula el backend da $0.
        </>
      ),
    },
    {
      id: "competitor-prices",
      label: "Precios de competencia",
      value: stats.competitor_price_count,
      filled: "Observaciones de precios de la competencia.",
      whyEmpty: (
        <>
          La task <code>pricing.sync_competitor_prices</code> existe pero es un
          placeholder declarado: loguea{" "}
          <em>"Competitor price sync not yet implemented"</em> y devuelve cero
          sin escribir nada. Fuera de <code>seed_data.py</code>, nada en el
          sistema puede llenar esta tabla.
        </>
      ),
    },
  ];

  return (
    <Panel
      title="Modulos sin datos todavia"
      lead={
        <>
          Estos numeros son reales y valen lo que valen. Se listan aparte, con el
          motivo, para que un cero no se confunda con un panel roto.
        </>
      }
    >
      <ul className="home-quiet">
        {modules.map((entry) => (
          <li key={entry.id} className="home-quiet__item">
            <span className="home-quiet__label">{entry.label}</span>
            <span
              className={
                entry.value === 0
                  ? "home-quiet__value home-quiet__value--empty"
                  : "home-quiet__value"
              }
            >
              {entry.value === 0 ? "vacio" : formatCount(entry.value)}
            </span>
            <span className="home-quiet__why">
              {entry.value === 0 ? entry.whyEmpty : entry.filled}
            </span>
          </li>
        ))}
      </ul>

      <Note>
        Los prompts de IA (<code>ai_prompts</code>) tampoco tienen filas, pero
        quedan afuera de esta lista por otra razon: su API ya existe (
        <code>/api/v1/prompts</code>) y el panel todavia no tiene una seccion a
        donde mandarte a verlos.
      </Note>
    </Panel>
  );
}
